export interface NigerianState {
  id: string;
  name: string;
  code: string;
  capital: string;
  zone: 'North Central' | 'North East' | 'North West' | 'South East' | 'South South' | 'South West';
  isFct?: boolean;
}

export interface NigerianLga {
  id: string;
  stateId: string;
  stateName: string;
  name: string;
  code?: string;
}

export interface NigerianLocationArea {
  id: string;
  stateId: string;
  stateName: string;
  lgaId: string;
  lgaName: string;
  name: string;
  type: 'capital_city' | 'city' | 'town' | 'district' | 'neighborhood' | 'commercial_hub';
  isCapitalCity?: boolean;
  latitude?: number;
  longitude?: number;
}

/**
 * EXACT 36 NIGERIAN STATES + FEDERAL CAPITAL TERRITORY (FCT)
 * Official administrative list with state capitals and geopolitical zones.
 */
export const NIGERIAN_STATES: NigerianState[] = [
  { id: 'state-ab', name: 'Abia', code: 'AB', capital: 'Umuahia', zone: 'South East' },
  { id: 'state-ad', name: 'Adamawa', code: 'AD', capital: 'Yola', zone: 'North East' },
  { id: 'state-ak', name: 'Akwa Ibom', code: 'AK', capital: 'Uyo', zone: 'South South' },
  { id: 'state-an', name: 'Anambra', code: 'AN', capital: 'Awka', zone: 'South East' },
  { id: 'state-ba', name: 'Bauchi', code: 'BA', capital: 'Bauchi', zone: 'North East' },
  { id: 'state-by', name: 'Bayelsa', code: 'BY', capital: 'Yenagoa', zone: 'South South' },
  { id: 'state-be', name: 'Benue', code: 'BE', capital: 'Makurdi', zone: 'North Central' },
  { id: 'state-bo', name: 'Borno', code: 'BO', capital: 'Maiduguri', zone: 'North East' },
  { id: 'state-cr', name: 'Cross River', code: 'CR', capital: 'Calabar', zone: 'South South' },
  { id: 'state-de', name: 'Delta', code: 'DE', capital: 'Asaba', zone: 'South South' },
  { id: 'state-eb', name: 'Ebonyi', code: 'EB', capital: 'Abakaliki', zone: 'South East' },
  { id: 'state-ed', name: 'Edo', code: 'ED', capital: 'Benin City', zone: 'South South' },
  { id: 'state-ek', name: 'Ekiti', code: 'EK', capital: 'Ado-Ekiti', zone: 'South West' },
  { id: 'state-en', name: 'Enugu', code: 'EN', capital: 'Enugu', zone: 'South East' },
  { id: 'state-go', name: 'Gombe', code: 'GO', capital: 'Gombe', zone: 'North East' },
  { id: 'state-im', name: 'Imo', code: 'IM', capital: 'Owerri', zone: 'South East' },
  { id: 'state-ji', name: 'Jigawa', code: 'JI', capital: 'Dutse', zone: 'North West' },
  { id: 'state-kd', name: 'Kaduna', code: 'KD', capital: 'Kaduna', zone: 'North West' },
  { id: 'state-kn', name: 'Kano', code: 'KN', capital: 'Kano', zone: 'North West' },
  { id: 'state-kt', name: 'Katsina', code: 'KT', capital: 'Katsina', zone: 'North West' },
  { id: 'state-kb', name: 'Kebbi', code: 'KB', capital: 'Birnin Kebbi', zone: 'North West' },
  { id: 'state-kg', name: 'Kogi', code: 'KG', capital: 'Lokoja', zone: 'North Central' },
  { id: 'state-kw', name: 'Kwara', code: 'KW', capital: 'Ilorin', zone: 'North Central' },
  { id: 'state-la', name: 'Lagos', code: 'LA', capital: 'Ikeja', zone: 'South West' },
  { id: 'state-na', name: 'Nasarawa', code: 'NA', capital: 'Lafia', zone: 'North Central' },
  { id: 'state-ni', name: 'Niger', code: 'NI', capital: 'Minna', zone: 'North Central' },
  { id: 'state-og', name: 'Ogun', code: 'OG', capital: 'Abeokuta', zone: 'South West' },
  { id: 'state-on', name: 'Ondo', code: 'ON', capital: 'Akure', zone: 'South West' },
  { id: 'state-os', name: 'Osun', code: 'OS', capital: 'Osogbo', zone: 'South West' },
  { id: 'state-oy', name: 'Oyo', code: 'OY', capital: 'Ibadan', zone: 'South West' },
  { id: 'state-pl', name: 'Plateau', code: 'PL', capital: 'Jos', zone: 'North Central' },
  { id: 'state-ri', name: 'Rivers', code: 'RI', capital: 'Port Harcourt', zone: 'South South' },
  { id: 'state-so', name: 'Sokoto', code: 'SO', capital: 'Sokoto', zone: 'North West' },
  { id: 'state-ta', name: 'Taraba', code: 'TA', capital: 'Jalingo', zone: 'North East' },
  { id: 'state-yo', name: 'Yobe', code: 'YO', capital: 'Damaturu', zone: 'North East' },
  { id: 'state-za', name: 'Zamfara', code: 'ZA', capital: 'Gusau', zone: 'North West' },
  { id: 'state-fc', name: 'Federal Capital Territory (FCT)', code: 'FC', capital: 'Abuja', zone: 'North Central', isFct: true },
];

/**
 * COMPLETE NIGERIAN LGAS (ALL 774 LOCAL GOVERNMENT AREAS & FCT AREA COUNCILS)
 * Authoritative administrative dataset mapped to exact state relationships.
 */
export const NIGERIAN_LGAS_MAP: Record<string, string[]> = {
  Abia: [
    'Aba North', 'Aba South', 'Arochukwu', 'Bende', 'Ikwuano', 'Isiala Ngwa North',
    'Isiala Ngwa South', 'Isuikwuato', 'Obi Ngwa', 'Ohafia', 'Osisioma', 'Ugwunagbo',
    'Ukwa East', 'Ukwa West', 'Umuahia North', 'Umuahia South', 'Umu Nneochi'
  ],
  Adamawa: [
    'Demsa', 'Fufore', 'Ganye', 'Girei', 'Gombi', 'Guyuk', 'Hong', 'Jada',
    'Lamurde', 'Madagali', 'Maiha', 'Mayo-Belwa', 'Michika', 'Mubi North',
    'Mubi South', 'Numan', 'Shelleng', 'Song', 'Toungo', 'Yola North', 'Yola South'
  ],
  'Akwa Ibom': [
    'Abak', 'Eastern Obolo', 'Eket', 'Esit Eket', 'Essien Udim', 'Etim Ekpo',
    'Etinan', 'Ibeno', 'Ibesikpo Asutan', 'Ibiono-Ibom', 'Ika', 'Ikono',
    'Ikot Abasi', 'Ikot Ekpene', 'Ini', 'Itu', 'Mbo', 'Mkpat-Enin', 'Nsit-Atai',
    'Nsit-Ibom', 'Nsit-Ubium', 'Obot Akara', 'Okobo', 'Onna', 'Oron', 'Oruk Anam',
    'Udung-Uko', 'Ukanafun', 'Uruan', 'Urue-Offong/Oruko', 'Uyo'
  ],
  Anambra: [
    'Aguata', 'Anambra East', 'Anambra West', 'Anaocha', 'Awka North', 'Awka South',
    'Ayamelum', 'Dunukofia', 'Ekwusigo', 'Idemili North', 'Idemili South', 'Ihiala',
    'Njikoka', 'Nnewi North', 'Nnewi South', 'Ogbaru', 'Onitsha North', 'Onitsha South',
    'Orumba North', 'Orumba South', 'Oyi'
  ],
  Bauchi: [
    'Alkaleri', 'Bauchi', 'Bogoro', 'Damban', 'Darazo', 'Dass', 'Gamawa',
    'Ganjuwa', 'Giade', 'Itas/Gadau', 'Jama\'are', 'Katagum', 'Kirfi', 'Misau',
    'Ningi', 'Shira', 'Tafawa Balewa', 'Toro', 'Warji', 'Zaki'
  ],
  Bayelsa: [
    'Brass', 'Ekeremor', 'Kolokuma/Opokuma', 'Nembe', 'Ogbia', 'Sagbama',
    'Southern Ijaw', 'Yenagoa'
  ],
  Benue: [
    'Ado', 'Agatu', 'Apa', 'Buruku', 'Gboko', 'Guma', 'Gwer East', 'Gwer West',
    'Katsina-Ala', 'Konshisha', 'Kwande', 'Logo', 'Makurdi', 'Obi', 'Ogbadibo',
    'Ohimini', 'Oju', 'Okpokwu', 'Otukpo', 'Tarka', 'Ukum', 'Ushongo', 'Vandeikya'
  ],
  Borno: [
    'Abadam', 'Askira/Uba', 'Bama', 'Bayo', 'Biu', 'Chibok', 'Damboa', 'Dikwa',
    'Gubio', 'Guzamala', 'Gwoza', 'Hawul', 'Jere', 'Kaga', 'Kala/Balge', 'Konduga',
    'Kukawa', 'Kwaya Kusar', 'Mafa', 'Magumeri', 'Maiduguri', 'Marte', 'Mobbar',
    'Monguno', 'Ngala', 'Nganzai', 'Shani'
  ],
  'Cross River': [
    'Abi', 'Akamkpa', 'Akpabuyo', 'Bakassi', 'Bekwarra', 'Biase', 'Boki',
    'Calabar Municipal', 'Calabar South', 'Etung', 'Ikom', 'Obanliku', 'Obubra',
    'Obudu', 'Odukpani', 'Ogoja', 'Yakuur', 'Yala'
  ],
  Delta: [
    'Aniocha North', 'Aniocha South', 'Bomadi', 'Burutu', 'Ethiope East', 'Ethiope West',
    'Ika North East', 'Ika South', 'Isoko North', 'Isoko South', 'Ndokwa East', 'Ndokwa West',
    'Okpe', 'Oshimili North', 'Oshimili South', 'Patani', 'Sapele', 'Udu',
    'Ughelli North', 'Ughelli South', 'Ukwuani', 'Uvwie', 'Warri North', 'Warri South',
    'Warri South West'
  ],
  Ebonyi: [
    'Abakaliki', 'Afikpo North', 'Afikpo South', 'Ebonyi', 'Ezza North', 'Ezza South',
    'Ikwo', 'Ishielu', 'Ivo', 'Izzi', 'Ohaozara', 'Ohaukwu', 'Onicha'
  ],
  Edo: [
    'Akoko-Edo', 'Egor', 'Esan Central', 'Esan North-East', 'Esan South-East', 'Esan West',
    'Etsako Central', 'Etsako East', 'Etsako West', 'Igueben', 'Ikpoba Okha', 'Oredo',
    'Orhionmwon', 'Ovia North-East', 'Ovia South-West', 'Owan East', 'Owan West', 'Uhunmwonde'
  ],
  Ekiti: [
    'Ado-Ekiti', 'Efon', 'Ekiti East', 'Ekiti South-West', 'Ekiti West', 'Emure',
    'Gbonyin', 'Ido-Osi', 'Ijero', 'Ikole', 'Ilejemeje', 'Irepodun/Ifelodun',
    'Ise/Orun', 'Moba', 'Oye', 'Ikere'
  ],
  Enugu: [
    'Aninri', 'Awgu', 'Enugu East', 'Enugu North', 'Enugu South', 'Ezeagu',
    'Igbo Etiti', 'Igbo Eze North', 'Igbo Eze South', 'Isi Uzo', 'Nkanu East',
    'Nkanu West', 'Nsukka', 'Oji River', 'Udenu', 'Udi', 'Uzo Uwani'
  ],
  Gombe: [
    'Akko', 'Balanga', 'Billiri', 'Dukku', 'Funakaye', 'Gombe',
    'Kaltungo', 'Kwami', 'Nafada', 'Shongom', 'Yamaltu/Deba'
  ],
  Imo: [
    'Aboh Mbaise', 'Ahiazu Mbaise', 'Ehime Mbano', 'Ezinihitte', 'Ideato North',
    'Ideato South', 'Ihitte/Uboma', 'Ikeduru', 'Isiala Mbano', 'Isu', 'Mbaitoli',
    'Ngor Okpala', 'Njaba', 'Nkwerre', 'Nwangele', 'Obowo', 'Oguta', 'Ohaji/Egbema',
    'Okigwe', 'Onuimo', 'Orlu', 'Orsu', 'Oru East', 'Oru West', 'Owerri Municipal',
    'Owerri North', 'Owerri West'
  ],
  Jigawa: [
    'Auyo', 'Babura', 'Biriniwa', 'Birnin Kudu', 'Buji', 'Dutse', 'Gagarawa',
    'Garki', 'Gumel', 'Guri', 'Gwaram', 'Gwiwa', 'Hadejia', 'Jahun', 'Kafin Hausa',
    'Kaugama', 'Kazaure', 'Kiri Kasama', 'Kiyawa', 'Maigatari', 'Malam Madori',
    'Miga', 'Ringim', 'Roni', 'Sule Tankarkar', 'Taura', 'Yankwashi'
  ],
  Kaduna: [
    'Birnin Gwari', 'Chikun', 'Giwa', 'Igabi', 'Ikara', 'Jaba', 'Jema\'a',
    'Kachia', 'Kaduna North', 'Kaduna South', 'Kagarko', 'Kajuru', 'Kaura',
    'Kauru', 'Kubau', 'Kudan', 'Lere', 'Makarfi', 'Sabon Gari', 'Sanga',
    'Soba', 'Zangon Kataf', 'Zaria'
  ],
  Kano: [
    'Ajingi', 'Albasu', 'Bagwai', 'Bebeji', 'Bichi', 'Bunkure', 'Dala',
    'Dambatta', 'Dawakin Kudu', 'Dawakin Tofa', 'Doguwa', 'Fagge', 'Gabasawa',
    'Garko', 'Garun Mallam', 'Gaya', 'Gezawa', 'Gwale', 'Gwarzo', 'Kabo',
    'Kano Municipal', 'Karaye', 'Kibiya', 'Kiru', 'Kumbotso', 'Kunchi', 'Kura',
    'Madobi', 'Makoda', 'Minjibir', 'Nasarawa', 'Rano', 'Rimin Gado', 'Rogo',
    'Shanono', 'Sumaila', 'Takai', 'Tarauni', 'Tofa', 'Tsanyawa', 'Tudun Wada',
    'Ungogo', 'Warawa', 'Wudil'
  ],
  Katsina: [
    'Bakori', 'Batagarawa', 'Batsari', 'Baure', 'Bindawa', 'Charanchi', 'Dandume',
    'Danja', 'Dan Musa', 'Daura', 'Dutsin Ma', 'Faskari', 'Funtua', 'Ingawa',
    'Jibia', 'Kafur', 'Kaita', 'Kankara', 'Kankia', 'Katsina', 'Kurfi', 'Kusada',
    'Mai\'Adua', 'Malumfashi', 'Mani', 'Mashi', 'Matazu', 'Musawa', 'Rimi',
    'Sabuwa', 'Safana', 'Sandamu', 'Zango', 'Dutsi'
  ],
  Kebbi: [
    'Aleiro', 'Arewa Dandi', 'Argungu', 'Augie', 'Bagudo', 'Birnin Kebbi', 'Bunza',
    'Dandi', 'Fakai', 'Gwandu', 'Jega', 'Kalgo', 'Koko/Besse', 'Maiyama', 'Ngaski',
    'Sakaba', 'Shanga', 'Suru', 'Danko/Wasagu', 'Yauri', 'Zuru'
  ],
  Kogi: [
    'Adavi', 'Ajaokuta', 'Ankpa', 'Bassa', 'Dekina', 'Ibaji', 'Idah',
    'Igalamela Odolu', 'Ijumu', 'Kabba/Bunu', 'Koton Karfe', 'Lokoja', 'Mopa Muro',
    'Ofu', 'Ogori/Magongo', 'Okehi', 'Okene', 'Olamaboro', 'Omala', 'Yagba East', 'Yagba West'
  ],
  Kwara: [
    'Asa', 'Baruten', 'Edu', 'Ekiti', 'Ifelodun', 'Ilorin East', 'Ilorin South',
    'Ilorin West', 'Irepodun', 'Isin', 'Kaiama', 'Moro', 'Offa', 'Oke Ero',
    'Oyun', 'Pategi'
  ],
  Lagos: [
    'Agege', 'Ajeromi-Ifelodun', 'Alimosho', 'Amuwo-Odofin', 'Apapa', 'Badagry',
    'Epe', 'Eti-Osa', 'Ibeju-Lekki', 'Ifako-Ijaiye', 'Ikeja', 'Ikorodu',
    'Kosofe', 'Lagos Island', 'Lagos Mainland', 'Mushin', 'Ojo', 'Oshodi-Isolo',
    'Shomolu', 'Surulere'
  ],
  Nasarawa: [
    'Akwanga', 'Awe', 'Doma', 'Karu', 'Keana', 'Keffi', 'Kokona', 'Lafia',
    'Nasarawa', 'Nasarawa Egon', 'Obi', 'Toto', 'Wamba'
  ],
  Niger: [
    'Agaie', 'Agwara', 'Bida', 'Borgu', 'Bosso', 'Chanchaga', 'Edati', 'Gbako',
    'Gurara', 'Katcha', 'Kontagora', 'Lapai', 'Lavun', 'Magama', 'Mariga',
    'Mashegu', 'Mokwa', 'Moya', 'Paikoro', 'Rafi', 'Rijau', 'Shiroro', 'Suleja',
    'Tafa', 'Wushishi'
  ],
  Ogun: [
    'Abeokuta North', 'Abeokuta South', 'Ado-Odo/Ota', 'Ewekoro', 'Ifo', 'Ijebu East',
    'Ijebu North', 'Ijebu North East', 'Ijebu Ode', 'Ikenne', 'Imeko Afon', 'Ipokia',
    'Obafemi Owode', 'Odogbolu', 'Odeda', 'Ogun Waterside', 'Remo North', 'Sagamu',
    'Yewa North', 'Yewa South'
  ],
  Ondo: [
    'Akoko North-East', 'Akoko North-West', 'Akoko South-East', 'Akoko South-West',
    'Akure North', 'Akure South', 'Ese Odo', 'Idanre', 'Ifedore', 'Ilaje',
    'Ile Oluji/Okeigbo', 'Irele', 'Odigbo', 'Okitipupa', 'Ondo East', 'Ondo West',
    'Ose', 'Owo'
  ],
  Osun: [
    'Atakunmosa East', 'Atakunmosa West', 'Aiyedaade', 'Aiyedire', 'Boluwaduro',
    'Boripe', 'Ede North', 'Ede South', 'Egbedore', 'Ejigbo', 'Ife Central',
    'Ife East', 'Ife North', 'Ife South', 'Ifedayo', 'Ifelodun', 'Ila',
    'Ilesa East', 'Ilesa West', 'Irepodun', 'Irewole', 'Isokan', 'Iwo', 'Obokun',
    'Odo Otin', 'Ola Oluwa', 'Olorunda', 'Oriade', 'Orolu', 'Osogbo'
  ],
  Oyo: [
    'Afijio', 'Akinyele', 'Atiba', 'Atisbo', 'Egbeda', 'Ibadan North',
    'Ibadan North-East', 'Ibadan North-West', 'Ibadan South-East', 'Ibadan South-West',
    'Ibarapa Central', 'Ibarapa East', 'Ibarapa North', 'Ido', 'Irepo', 'Iseyin',
    'Itesiwaju', 'Iwajowa', 'Kajola', 'Lagelu', 'Ogbomosho North', 'Ogbomosho South',
    'Ogo Oluwa', 'Olorunsogo', 'Oluyole', 'Ona Ara', 'Orelope', 'Ori Ire',
    'Oyo East', 'Oyo West', 'Saki East', 'Saki West', 'Surulere'
  ],
  Plateau: [
    'Barkin Ladi', 'Bassa', 'Bokkos', 'Jos East', 'Jos North', 'Jos South',
    'Kanam', 'Kanke', 'Langtang North', 'Langtang South', 'Mangu', 'Mikang',
    'Pankshin', 'Qua\'an Pan', 'Riyom', 'Shendam', 'Wase'
  ],
  Rivers: [
    'Abua/Odual', 'Ahoada East', 'Ahoada West', 'Akuku-Toru', 'Andoni', 'Asari-Toru',
    'Bonny', 'Degema', 'Eleme', 'Emohua', 'Etche', 'Gokana', 'Ikwerre', 'Khana',
    'Obio/Akpor', 'Ogba/Egbema/Ndoni', 'Ogu/Bolo', 'Okrika', 'Omuma', 'Opobo/Nkoro',
    'Oyigbo', 'Port Harcourt', 'Tai'
  ],
  Sokoto: [
    'Binji', 'Bodinga', 'Dange Shuni', 'Gada', 'Goronyo', 'Gudu', 'Gwadabawa',
    'Illela', 'Isa', 'Kebbe', 'Kware', 'Rabah', 'Sabon Birni', 'Shagari',
    'Silame', 'Sokoto North', 'Sokoto South', 'Tambuwal', 'Tangaza', 'Tureta',
    'Wamako', 'Wurno', 'Yabo'
  ],
  Taraba: [
    'Ardo Kola', 'Bali', 'Donga', 'Gashaka', 'Gassol', 'Ibi', 'Jalingo',
    'Karim Lamido', 'Kurmi', 'Lau', 'Sardauna', 'Takum', 'Ussa', 'Wukari',
    'Yorro', 'Zing'
  ],
  Yobe: [
    'Bade', 'Bursari', 'Damaturu', 'Fika', 'Fune', 'Geidam', 'Gujba', 'Gulani',
    'Jakusko', 'Karasuwa', 'Machina', 'Nangere', 'Nguru', 'Potiskum', 'Tarmuwa',
    'Yunusari', 'Yusufari'
  ],
  Zamfara: [
    'Anka', 'Bakura', 'Birnin Magaji/Kiyaw', 'Bukkuyum', 'Bungudu', 'Gummi',
    'Gusau', 'Kaura Namoda', 'Maradun', 'Maru', 'Shinkafi', 'Talata Mafara',
    'Tsafe', 'Zurmi'
  ],
  'Federal Capital Territory (FCT)': [
    'Abaji', 'Abuja Municipal Area Council', 'Bwari', 'Gwagwalada', 'Kuje', 'Kwali'
  ]
};

// Aliases for user flexibility (e.g. "FCT" -> "Federal Capital Territory (FCT)")
export const STATE_ALIASES: Record<string, string> = {
  fct: 'Federal Capital Territory (FCT)',
  abuja: 'Federal Capital Territory (FCT)',
  'abuja (fct)': 'Federal Capital Territory (FCT)',
  'federal capital territory': 'Federal Capital Territory (FCT)',
  'fct abuja': 'Federal Capital Territory (FCT)',
};

/**
 * REPRESENTATIVE CITIES, TOWNS, DISTRICTS, AND NEIGHBORHOODS (LEVEL 3)
 * Mapped to their official LGA and State.
 */
export const NIGERIAN_AREAS: NigerianLocationArea[] = [
  // --- DELTA STATE ---
  // Capital: Asaba (Oshimili South)
  { id: 'loc-de-01', stateId: 'state-de', stateName: 'Delta', lgaId: 'lga-de-oshimili-south', lgaName: 'Oshimili South', name: 'Asaba', type: 'capital_city', isCapitalCity: true },
  { id: 'loc-de-02', stateId: 'state-de', stateName: 'Delta', lgaId: 'lga-de-oshimili-south', lgaName: 'Oshimili South', name: 'Cable Point', type: 'neighborhood' },
  { id: 'loc-de-03', stateId: 'state-de', stateName: 'Delta', lgaId: 'lga-de-oshimili-south', lgaName: 'Oshimili South', name: 'Okwe', type: 'district' },
  { id: 'loc-de-04', stateId: 'state-de', stateName: 'Delta', lgaId: 'lga-de-oshimili-south', lgaName: 'Oshimili South', name: 'Anwai', type: 'district' },
  { id: 'loc-de-05', stateId: 'state-de', stateName: 'Delta', lgaId: 'lga-de-oshimili-south', lgaName: 'Oshimili South', name: 'GRA Asaba', type: 'neighborhood' },
  { id: 'loc-de-06', stateId: 'state-de', stateName: 'Delta', lgaId: 'lga-de-oshimili-north', lgaName: 'Oshimili North', name: 'Okpanam', type: 'town' },
  { id: 'loc-de-07', stateId: 'state-de', stateName: 'Delta', lgaId: 'lga-de-oshimili-north', lgaName: 'Oshimili North', name: 'Ibusa', type: 'town' },
  { id: 'loc-de-08', stateId: 'state-de', stateName: 'Delta', lgaId: 'lga-de-oshimili-north', lgaName: 'Oshimili North', name: 'Akwukwu-Igbo', type: 'town' },
  { id: 'loc-de-09', stateId: 'state-de', stateName: 'Delta', lgaId: 'lga-de-oshimili-north', lgaName: 'Oshimili North', name: 'Issele-Azagba', type: 'town' },
  { id: 'loc-de-10', stateId: 'state-de', stateName: 'Delta', lgaId: 'lga-de-aniocha-north', lgaName: 'Aniocha North', name: 'Issele-Uku', type: 'town' },
  { id: 'loc-de-11', stateId: 'state-de', stateName: 'Delta', lgaId: 'lga-de-aniocha-north', lgaName: 'Aniocha North', name: 'Onicha-Olona', type: 'town' },
  { id: 'loc-de-12', stateId: 'state-de', stateName: 'Delta', lgaId: 'lga-de-aniocha-south', lgaName: 'Aniocha South', name: 'Ogwashi-Uku', type: 'town' },
  { id: 'loc-de-13', stateId: 'state-de', stateName: 'Delta', lgaId: 'lga-de-aniocha-south', lgaName: 'Aniocha South', name: 'Ubulu-Uku', type: 'town' },
  { id: 'loc-de-14', stateId: 'state-de', stateName: 'Delta', lgaId: 'lga-de-warri-south', lgaName: 'Warri South', name: 'Warri', type: 'city' },
  { id: 'loc-de-15', stateId: 'state-de', stateName: 'Delta', lgaId: 'lga-de-warri-south', lgaName: 'Warri South', name: 'GRA Warri', type: 'neighborhood' },
  { id: 'loc-de-16', stateId: 'state-de', stateName: 'Delta', lgaId: 'lga-de-warri-south', lgaName: 'Warri South', name: 'Ogunu', type: 'district' },
  { id: 'loc-de-17', stateId: 'state-de', stateName: 'Delta', lgaId: 'lga-de-uvwie', lgaName: 'Uvwie', name: 'Effurun', type: 'city' },
  { id: 'loc-de-18', stateId: 'state-de', stateName: 'Delta', lgaId: 'lga-de-uvwie', lgaName: 'Uvwie', name: 'PTI Area', type: 'neighborhood' },
  { id: 'loc-de-19', stateId: 'state-de', stateName: 'Delta', lgaId: 'lga-de-sapele', lgaName: 'Sapele', name: 'Sapele', type: 'city' },
  { id: 'loc-de-20', stateId: 'state-de', stateName: 'Delta', lgaId: 'lga-de-ughelli-north', lgaName: 'Ughelli North', name: 'Ughelli', type: 'city' },
  { id: 'loc-de-21', stateId: 'state-de', stateName: 'Delta', lgaId: 'lga-de-ughelli-north', lgaName: 'Ughelli North', name: 'Agbarho', type: 'town' },
  { id: 'loc-de-22', stateId: 'state-de', stateName: 'Delta', lgaId: 'lga-de-ika-south', lgaName: 'Ika South', name: 'Agbor', type: 'city' },

  // --- LAGOS STATE ---
  // Capital: Ikeja (Ikeja LGA)
  { id: 'loc-la-01', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-ikeja', lgaName: 'Ikeja', name: 'Ikeja', type: 'capital_city', isCapitalCity: true },
  { id: 'loc-la-02', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-ikeja', lgaName: 'Ikeja', name: 'Allen Avenue', type: 'commercial_hub' },
  { id: 'loc-la-03', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-ikeja', lgaName: 'Ikeja', name: 'Opebi', type: 'neighborhood' },
  { id: 'loc-la-04', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-ikeja', lgaName: 'Ikeja', name: 'Alausa (Secretariat)', type: 'district' },
  { id: 'loc-la-05', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-ikeja', lgaName: 'Ikeja', name: 'GRA Ikeja', type: 'neighborhood' },
  { id: 'loc-la-06', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-ikeja', lgaName: 'Ikeja', name: 'Computer Village', type: 'commercial_hub' },
  { id: 'loc-la-07', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-ikeja', lgaName: 'Ikeja', name: 'Maryland', type: 'district' },
  { id: 'loc-la-08', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-ikeja', lgaName: 'Ikeja', name: 'Agidingbi', type: 'district' },
  { id: 'loc-la-09', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-eti-osa', lgaName: 'Eti-Osa', name: 'Lekki Phase 1', type: 'neighborhood' },
  { id: 'loc-la-10', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-eti-osa', lgaName: 'Eti-Osa', name: 'Victoria Island', type: 'commercial_hub' },
  { id: 'loc-la-11', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-eti-osa', lgaName: 'Eti-Osa', name: 'Ikoyi', type: 'neighborhood' },
  { id: 'loc-la-12', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-eti-osa', lgaName: 'Eti-Osa', name: 'Oniru', type: 'neighborhood' },
  { id: 'loc-la-13', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-eti-osa', lgaName: 'Eti-Osa', name: 'Ajah', type: 'district' },
  { id: 'loc-la-14', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-eti-osa', lgaName: 'Eti-Osa', name: 'Ikate Elegushi', type: 'neighborhood' },
  { id: 'loc-la-15', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-eti-osa', lgaName: 'Eti-Osa', name: 'Chevron / Lekki Conservation', type: 'neighborhood' },
  { id: 'loc-la-16', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-surulere', lgaName: 'Surulere', name: 'Surulere', type: 'district' },
  { id: 'loc-la-17', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-surulere', lgaName: 'Surulere', name: 'Bode Thomas', type: 'commercial_hub' },
  { id: 'loc-la-18', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-surulere', lgaName: 'Surulere', name: 'Adeniran Ogunsanya', type: 'neighborhood' },
  { id: 'loc-la-19', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-surulere', lgaName: 'Surulere', name: 'Ojuelegba', type: 'district' },
  { id: 'loc-la-20', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-lagos-mainland', lgaName: 'Lagos Mainland', name: 'Yaba', type: 'district' },
  { id: 'loc-la-21', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-lagos-mainland', lgaName: 'Lagos Mainland', name: 'Ebute Metta', type: 'district' },
  { id: 'loc-la-22', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-lagos-mainland', lgaName: 'Lagos Mainland', name: 'Akoka (UNILAG)', type: 'district' },
  { id: 'loc-la-23', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-kosofe', lgaName: 'Kosofe', name: 'Magodo Phase 1 & 2', type: 'neighborhood' },
  { id: 'loc-la-24', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-kosofe', lgaName: 'Kosofe', name: 'Gbagada', type: 'neighborhood' },
  { id: 'loc-la-25', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-kosofe', lgaName: 'Kosofe', name: 'Ogudu GRA', type: 'neighborhood' },
  { id: 'loc-la-26', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-kosofe', lgaName: 'Kosofe', name: 'Ojota', type: 'district' },
  { id: 'loc-la-27', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-alimosho', lgaName: 'Alimosho', name: 'Egbeda', type: 'district' },
  { id: 'loc-la-28', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-alimosho', lgaName: 'Alimosho', name: 'Iyana Ipaja', type: 'district' },
  { id: 'loc-la-29', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-alimosho', lgaName: 'Alimosho', name: 'Akowonjo', type: 'neighborhood' },
  { id: 'loc-la-30', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-alimosho', lgaName: 'Alimosho', name: 'Ikotun', type: 'district' },
  { id: 'loc-la-31', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-oshodi-isolo', lgaName: 'Oshodi-Isolo', name: 'Ajao Estate', type: 'neighborhood' },
  { id: 'loc-la-32', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-oshodi-isolo', lgaName: 'Oshodi-Isolo', name: 'Isolo', type: 'district' },
  { id: 'loc-la-33', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-oshodi-isolo', lgaName: 'Oshodi-Isolo', name: 'Okota', type: 'neighborhood' },
  { id: 'loc-la-34', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-ikorodu', lgaName: 'Ikorodu', name: 'Ikorodu Town', type: 'city' },
  { id: 'loc-la-35', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-ibeju-lekki', lgaName: 'Ibeju-Lekki', name: 'Sangotedo', type: 'neighborhood' },
  { id: 'loc-la-36', stateId: 'state-la', stateName: 'Lagos', lgaId: 'lga-la-ibeju-lekki', lgaName: 'Ibeju-Lekki', name: 'Eleko / Free Trade Zone', type: 'district' },

  // --- FEDERAL CAPITAL TERRITORY (FCT) ---
  // Capital: Abuja (Abuja Municipal Area Council)
  { id: 'loc-fc-01', stateId: 'state-fc', stateName: 'Federal Capital Territory (FCT)', lgaId: 'lga-fc-amac', lgaName: 'Abuja Municipal Area Council', name: 'Abuja', type: 'capital_city', isCapitalCity: true },
  { id: 'loc-fc-02', stateId: 'state-fc', stateName: 'Federal Capital Territory (FCT)', lgaId: 'lga-fc-amac', lgaName: 'Abuja Municipal Area Council', name: 'Wuse II', type: 'commercial_hub' },
  { id: 'loc-fc-03', stateId: 'state-fc', stateName: 'Federal Capital Territory (FCT)', lgaId: 'lga-fc-amac', lgaName: 'Abuja Municipal Area Council', name: 'Wuse Zone 1-7', type: 'district' },
  { id: 'loc-fc-04', stateId: 'state-fc', stateName: 'Federal Capital Territory (FCT)', lgaId: 'lga-fc-amac', lgaName: 'Abuja Municipal Area Council', name: 'Maitama', type: 'neighborhood' },
  { id: 'loc-fc-05', stateId: 'state-fc', stateName: 'Federal Capital Territory (FCT)', lgaId: 'lga-fc-amac', lgaName: 'Abuja Municipal Area Council', name: 'Garki 1 & 2', type: 'district' },
  { id: 'loc-fc-06', stateId: 'state-fc', stateName: 'Federal Capital Territory (FCT)', lgaId: 'lga-fc-amac', lgaName: 'Abuja Municipal Area Council', name: 'Asokoro', type: 'neighborhood' },
  { id: 'loc-fc-07', stateId: 'state-fc', stateName: 'Federal Capital Territory (FCT)', lgaId: 'lga-fc-amac', lgaName: 'Abuja Municipal Area Council', name: 'Jabi', type: 'district' },
  { id: 'loc-fc-08', stateId: 'state-fc', stateName: 'Federal Capital Territory (FCT)', lgaId: 'lga-fc-amac', lgaName: 'Abuja Municipal Area Council', name: 'Utako', type: 'district' },
  { id: 'loc-fc-09', stateId: 'state-fc', stateName: 'Federal Capital Territory (FCT)', lgaId: 'lga-fc-amac', lgaName: 'Abuja Municipal Area Council', name: 'Gwarinpa', type: 'neighborhood' },
  { id: 'loc-fc-10', stateId: 'state-fc', stateName: 'Federal Capital Territory (FCT)', lgaId: 'lga-fc-amac', lgaName: 'Abuja Municipal Area Council', name: 'Guzape', type: 'neighborhood' },
  { id: 'loc-fc-11', stateId: 'state-fc', stateName: 'Federal Capital Territory (FCT)', lgaId: 'lga-fc-amac', lgaName: 'Abuja Municipal Area Council', name: 'Central Business District (CBD)', type: 'commercial_hub' },
  { id: 'loc-fc-12', stateId: 'state-fc', stateName: 'Federal Capital Territory (FCT)', lgaId: 'lga-fc-amac', lgaName: 'Abuja Municipal Area Council', name: 'Lugbe', type: 'district' },
  { id: 'loc-fc-13', stateId: 'state-fc', stateName: 'Federal Capital Territory (FCT)', lgaId: 'lga-fc-amac', lgaName: 'Abuja Municipal Area Council', name: 'Lokogoma', type: 'neighborhood' },
  { id: 'loc-fc-14', stateId: 'state-fc', stateName: 'Federal Capital Territory (FCT)', lgaId: 'lga-fc-amac', lgaName: 'Abuja Municipal Area Council', name: 'Apo / Apo Resettlement', type: 'district' },
  { id: 'loc-fc-15', stateId: 'state-fc', stateName: 'Federal Capital Territory (FCT)', lgaId: 'lga-fc-bwari', lgaName: 'Bwari', name: 'Kubwa', type: 'town' },
  { id: 'loc-fc-16', stateId: 'state-fc', stateName: 'Federal Capital Territory (FCT)', lgaId: 'lga-fc-bwari', lgaName: 'Bwari', name: 'Dutse Alhaji', type: 'district' },
  { id: 'loc-fc-17', stateId: 'state-fc', stateName: 'Federal Capital Territory (FCT)', lgaId: 'lga-fc-bwari', lgaName: 'Bwari', name: 'Dawaki', type: 'neighborhood' },
  { id: 'loc-fc-18', stateId: 'state-fc', stateName: 'Federal Capital Territory (FCT)', lgaId: 'lga-fc-gwagwalada', lgaName: 'Gwagwalada', name: 'Gwagwalada Town', type: 'town' },
  { id: 'loc-fc-19', stateId: 'state-fc', stateName: 'Federal Capital Territory (FCT)', lgaId: 'lga-fc-kuje', lgaName: 'Kuje', name: 'Kuje Town', type: 'town' },

  // --- EDO STATE ---
  // Capital: Benin City (Oredo LGA)
  { id: 'loc-ed-01', stateId: 'state-ed', stateName: 'Edo', lgaId: 'lga-ed-oredo', lgaName: 'Oredo', name: 'Benin City', type: 'capital_city', isCapitalCity: true },
  { id: 'loc-ed-02', stateId: 'state-ed', stateName: 'Edo', lgaId: 'lga-ed-oredo', lgaName: 'Oredo', name: 'GRA Benin', type: 'neighborhood' },
  { id: 'loc-ed-03', stateId: 'state-ed', stateName: 'Edo', lgaId: 'lga-ed-oredo', lgaName: 'Oredo', name: 'Ring Road / King Square', type: 'commercial_hub' },
  { id: 'loc-ed-04', stateId: 'state-ed', stateName: 'Edo', lgaId: 'lga-ed-oredo', lgaName: 'Oredo', name: 'Airport Road', type: 'district' },
  { id: 'loc-ed-05', stateId: 'state-ed', stateName: 'Edo', lgaId: 'lga-ed-ikpoba-okha', lgaName: 'Ikpoba Okha', name: 'Ikpoba Hill', type: 'district' },
  { id: 'loc-ed-06', stateId: 'state-ed', stateName: 'Edo', lgaId: 'lga-ed-ikpoba-okha', lgaName: 'Ikpoba Okha', name: 'Upper Sakponba', type: 'district' },
  { id: 'loc-ed-07', stateId: 'state-ed', stateName: 'Edo', lgaId: 'lga-ed-egor', lgaName: 'Egor', name: 'Uselu', type: 'district' },
  { id: 'loc-ed-08', stateId: 'state-ed', stateName: 'Edo', lgaId: 'lga-ed-egor', lgaName: 'Egor', name: 'Ugbowo (UNIBEN)', type: 'district' },
  { id: 'loc-ed-09', stateId: 'state-ed', stateName: 'Edo', lgaId: 'lga-ed-esan-west', lgaName: 'Esan West', name: 'Ekpoma', type: 'town' },
  { id: 'loc-ed-10', stateId: 'state-ed', stateName: 'Edo', lgaId: 'lga-ed-etsako-west', lgaName: 'Etsako West', name: 'Auchi', type: 'city' },

  // --- RIVERS STATE ---
  // Capital: Port Harcourt (Port Harcourt LGA)
  { id: 'loc-ri-01', stateId: 'state-ri', stateName: 'Rivers', lgaId: 'lga-ri-port-harcourt', lgaName: 'Port Harcourt', name: 'Port Harcourt', type: 'capital_city', isCapitalCity: true },
  { id: 'loc-ri-02', stateId: 'state-ri', stateName: 'Rivers', lgaId: 'lga-ri-port-harcourt', lgaName: 'Port Harcourt', name: 'Old GRA', type: 'neighborhood' },
  { id: 'loc-ri-03', stateId: 'state-ri', stateName: 'Rivers', lgaId: 'lga-ri-port-harcourt', lgaName: 'Port Harcourt', name: 'New GRA (Phases 1-4)', type: 'neighborhood' },
  { id: 'loc-ri-04', stateId: 'state-ri', stateName: 'Rivers', lgaId: 'lga-ri-port-harcourt', lgaName: 'Port Harcourt', name: 'Diobu', type: 'district' },
  { id: 'loc-ri-05', stateId: 'state-ri', stateName: 'Rivers', lgaId: 'lga-ri-port-harcourt', lgaName: 'Port Harcourt', name: 'D-Line', type: 'commercial_hub' },
  { id: 'loc-ri-06', stateId: 'state-ri', stateName: 'Rivers', lgaId: 'lga-ri-port-harcourt', lgaName: 'Port Harcourt', name: 'Borokiri', type: 'district' },
  { id: 'loc-ri-07', stateId: 'state-ri', stateName: 'Rivers', lgaId: 'lga-ri-obio-akpor', lgaName: 'Obio/Akpor', name: 'Rumuokoro', type: 'district' },
  { id: 'loc-ri-08', stateId: 'state-ri', stateName: 'Rivers', lgaId: 'lga-ri-obio-akpor', lgaName: 'Obio/Akpor', name: 'Rumuola', type: 'district' },
  { id: 'loc-ri-09', stateId: 'state-ri', stateName: 'Rivers', lgaId: 'lga-ri-obio-akpor', lgaName: 'Obio/Akpor', name: 'Trans-Amadi Industrial', type: 'commercial_hub' },
  { id: 'loc-ri-10', stateId: 'state-ri', stateName: 'Rivers', lgaId: 'lga-ri-obio-akpor', lgaName: 'Obio/Akpor', name: 'Woji', type: 'neighborhood' },
  { id: 'loc-ri-11', stateId: 'state-ri', stateName: 'Rivers', lgaId: 'lga-ri-obio-akpor', lgaName: 'Obio/Akpor', name: 'Choba (Uniport)', type: 'district' },
  { id: 'loc-ri-12', stateId: 'state-ri', stateName: 'Rivers', lgaId: 'lga-ri-eleme', lgaName: 'Eleme', name: 'Eleme / Petrochemical Area', type: 'district' },

  // --- OYO STATE ---
  // Capital: Ibadan (Ibadan North / Central)
  { id: 'loc-oy-01', stateId: 'state-oy', stateName: 'Oyo', lgaId: 'lga-oy-ibadan-north', lgaName: 'Ibadan North', name: 'Ibadan', type: 'capital_city', isCapitalCity: true },
  { id: 'loc-oy-02', stateId: 'state-oy', stateName: 'Oyo', lgaId: 'lga-oy-ibadan-north', lgaName: 'Ibadan North', name: 'Bodija', type: 'neighborhood' },
  { id: 'loc-oy-03', stateId: 'state-oy', stateName: 'Oyo', lgaId: 'lga-oy-ibadan-north', lgaName: 'Ibadan North', name: 'Agbowo (UI Area)', type: 'district' },
  { id: 'loc-oy-04', stateId: 'state-oy', stateName: 'Oyo', lgaId: 'lga-oy-ibadan-north-west', lgaName: 'Ibadan North-West', name: 'Dugbe Commercial Area', type: 'commercial_hub' },
  { id: 'loc-oy-05', stateId: 'state-oy', stateName: 'Oyo', lgaId: 'lga-oy-ibadan-south-west', lgaName: 'Ibadan South-West', name: 'Ring Road', type: 'district' },
  { id: 'loc-oy-06', stateId: 'state-oy', stateName: 'Oyo', lgaId: 'lga-oy-ibadan-south-west', lgaName: 'Ibadan South-West', name: 'Oluyole Estate', type: 'neighborhood' },
  { id: 'loc-oy-07', stateId: 'state-oy', stateName: 'Oyo', lgaId: 'lga-oy-ogbomosho-north', lgaName: 'Ogbomosho North', name: 'Ogbomosho', type: 'city' },

  // --- ENUGU STATE ---
  // Capital: Enugu (Enugu North / East / South)
  { id: 'loc-en-01', stateId: 'state-en', stateName: 'Enugu', lgaId: 'lga-en-enugu-north', lgaName: 'Enugu North', name: 'Enugu', type: 'capital_city', isCapitalCity: true },
  { id: 'loc-en-02', stateId: 'state-en', stateName: 'Enugu', lgaId: 'lga-en-enugu-north', lgaName: 'Enugu North', name: 'Independence Layout', type: 'neighborhood' },
  { id: 'loc-en-03', stateId: 'state-en', stateName: 'Enugu', lgaId: 'lga-en-enugu-north', lgaName: 'Enugu North', name: 'New Haven', type: 'neighborhood' },
  { id: 'loc-en-04', stateId: 'state-en', stateName: 'Enugu', lgaId: 'lga-en-enugu-north', lgaName: 'Enugu North', name: 'GRA Enugu', type: 'neighborhood' },
  { id: 'loc-en-05', stateId: 'state-en', stateName: 'Enugu', lgaId: 'lga-en-enugu-east', lgaName: 'Enugu East', name: 'Trans-Ekulu', type: 'neighborhood' },
  { id: 'loc-en-06', stateId: 'state-en', stateName: 'Enugu', lgaId: 'lga-en-enugu-south', lgaName: 'Enugu South', name: 'Achara Layout', type: 'district' },
  { id: 'loc-en-07', stateId: 'state-en', stateName: 'Enugu', lgaId: 'lga-en-nsukka', lgaName: 'Nsukka', name: 'Nsukka (UNN)', type: 'city' },

  // --- ANAMBRA STATE ---
  // Capital: Awka (Awka South)
  { id: 'loc-an-01', stateId: 'state-an', stateName: 'Anambra', lgaId: 'lga-an-awka-south', lgaName: 'Awka South', name: 'Awka', type: 'capital_city', isCapitalCity: true },
  { id: 'loc-an-02', stateId: 'state-an', stateName: 'Anambra', lgaId: 'lga-an-awka-south', lgaName: 'Awka South', name: 'Ifite (UNIZIK Area)', type: 'district' },
  { id: 'loc-an-03', stateId: 'state-an', stateName: 'Anambra', lgaId: 'lga-an-awka-south', lgaName: 'Awka South', name: 'Amawbia', type: 'town' },
  { id: 'loc-an-04', stateId: 'state-an', stateName: 'Anambra', lgaId: 'lga-an-onitsha-north', lgaName: 'Onitsha North', name: 'Onitsha', type: 'city' },
  { id: 'loc-an-05', stateId: 'state-an', stateName: 'Anambra', lgaId: 'lga-an-onitsha-north', lgaName: 'Onitsha North', name: 'GRA Onitsha', type: 'neighborhood' },
  { id: 'loc-an-06', stateId: 'state-an', stateName: 'Anambra', lgaId: 'lga-an-onitsha-south', lgaName: 'Onitsha South', name: 'Main Market / Bridgehead', type: 'commercial_hub' },
  { id: 'loc-an-07', stateId: 'state-an', stateName: 'Anambra', lgaId: 'lga-an-nnewi-north', lgaName: 'Nnewi North', name: 'Nnewi', type: 'city' },

  // --- KANO STATE ---
  // Capital: Kano (Kano Municipal)
  { id: 'loc-kn-01', stateId: 'state-kn', stateName: 'Kano', lgaId: 'lga-kn-kano-municipal', lgaName: 'Kano Municipal', name: 'Kano', type: 'capital_city', isCapitalCity: true },
  { id: 'loc-kn-02', stateId: 'state-kn', stateName: 'Kano', lgaId: 'lga-kn-kano-municipal', lgaName: 'Kano Municipal', name: 'Kano City Center / Kurmi', type: 'commercial_hub' },
  { id: 'loc-kn-03', stateId: 'state-kn', stateName: 'Kano', lgaId: 'lga-kn-fagge', lgaName: 'Fagge', name: 'Fagge / Sabon Gari', type: 'commercial_hub' },
  { id: 'loc-kn-04', stateId: 'state-kn', stateName: 'Kano', lgaId: 'lga-kn-nasarawa', lgaName: 'Nasarawa', name: 'Bompai Industrial Area', type: 'commercial_hub' },
  { id: 'loc-kn-05', stateId: 'state-kn', stateName: 'Kano', lgaId: 'lga-kn-nasarawa', lgaName: 'Nasarawa', name: 'Nasarawa GRA', type: 'neighborhood' },

  // --- KADUNA STATE ---
  // Capital: Kaduna (Kaduna North / South)
  { id: 'loc-kd-01', stateId: 'state-kd', stateName: 'Kaduna', lgaId: 'lga-kd-kaduna-north', lgaName: 'Kaduna North', name: 'Kaduna', type: 'capital_city', isCapitalCity: true },
  { id: 'loc-kd-02', stateId: 'state-kd', stateName: 'Kaduna', lgaId: 'lga-kd-kaduna-north', lgaName: 'Kaduna North', name: 'Malali GRA', type: 'neighborhood' },
  { id: 'loc-kd-03', stateId: 'state-kd', stateName: 'Kaduna', lgaId: 'lga-kd-kaduna-north', lgaName: 'Kaduna North', name: 'Ungwan Rimi', type: 'district' },
  { id: 'loc-kd-04', stateId: 'state-kd', stateName: 'Kaduna', lgaId: 'lga-kd-kaduna-south', lgaName: 'Kaduna South', name: 'Barnawa', type: 'neighborhood' },
  { id: 'loc-kd-05', stateId: 'state-kd', stateName: 'Kaduna', lgaId: 'lga-kd-zaria', lgaName: 'Zaria', name: 'Zaria', type: 'city' },
  { id: 'loc-kd-06', stateId: 'state-kd', stateName: 'Kaduna', lgaId: 'lga-kd-zaria', lgaName: 'Zaria', name: 'Samaru (ABU Area)', type: 'district' },

  // --- OGUN STATE ---
  // Capital: Abeokuta (Abeokuta South / North)
  { id: 'loc-og-01', stateId: 'state-og', stateName: 'Ogun', lgaId: 'lga-og-abeokuta-south', lgaName: 'Abeokuta South', name: 'Abeokuta', type: 'capital_city', isCapitalCity: true },
  { id: 'loc-og-02', stateId: 'state-og', stateName: 'Ogun', lgaId: 'lga-og-ado-odo-ota', lgaName: 'Ado-Odo/Ota', name: 'Ota Industrial', type: 'city' },
  { id: 'loc-og-03', stateId: 'state-og', stateName: 'Ogun', lgaId: 'lga-og-sagamu', lgaName: 'Sagamu', name: 'Sagamu Interchange Area', type: 'city' },
  { id: 'loc-og-04', stateId: 'state-og', stateName: 'Ogun', lgaId: 'lga-og-ijebu-ode', lgaName: 'Ijebu Ode', name: 'Ijebu Ode', type: 'city' },
  { id: 'loc-og-05', stateId: 'state-og', stateName: 'Ogun', lgaId: 'lga-og-obafemi-owode', lgaName: 'Obafemi Owode', name: 'Mowe / Ibafo', type: 'town' },

  // --- ONDO STATE ---
  // Capital: Akure (Akure South)
  { id: 'loc-on-01', stateId: 'state-on', stateName: 'Ondo', lgaId: 'lga-on-akure-south', lgaName: 'Akure South', name: 'Akure', type: 'capital_city', isCapitalCity: true },
  { id: 'loc-on-02', stateId: 'state-on', stateName: 'Ondo', lgaId: 'lga-on-akure-south', lgaName: 'Akure South', name: 'Alagbaka GRA', type: 'neighborhood' },
  { id: 'loc-on-03', stateId: 'state-on', stateName: 'Ondo', lgaId: 'lga-on-ondo-west', lgaName: 'Ondo West', name: 'Ondo Town', type: 'city' },

  // --- KWARA STATE ---
  // Capital: Ilorin (Ilorin West / South / East)
  { id: 'loc-kw-01', stateId: 'state-kw', stateName: 'Kwara', lgaId: 'lga-kw-ilorin-south', lgaName: 'Ilorin South', name: 'Ilorin', type: 'capital_city', isCapitalCity: true },
  { id: 'loc-kw-02', stateId: 'state-kw', stateName: 'Kwara', lgaId: 'lga-kw-ilorin-south', lgaName: 'Ilorin South', name: 'GRA Ilorin', type: 'neighborhood' },
  { id: 'loc-kw-03', stateId: 'state-kw', stateName: 'Kwara', lgaId: 'lga-kw-offa', lgaName: 'Offa', name: 'Offa', type: 'town' },

  // --- ABIA STATE ---
  // Capital: Umuahia (Umuahia North / South)
  { id: 'loc-ab-01', stateId: 'state-ab', stateName: 'Abia', lgaId: 'lga-ab-umuahia-north', lgaName: 'Umuahia North', name: 'Umuahia', type: 'capital_city', isCapitalCity: true },
  { id: 'loc-ab-02', stateId: 'state-ab', stateName: 'Abia', lgaId: 'lga-ab-aba-south', lgaName: 'Aba South', name: 'Aba', type: 'city' },
  { id: 'loc-ab-03', stateId: 'state-ab', stateName: 'Abia', lgaId: 'lga-ab-aba-north', lgaName: 'Aba North', name: 'Ariaria Market District', type: 'commercial_hub' },

  // --- AKWA IBOM STATE ---
  // Capital: Uyo (Uyo LGA)
  { id: 'loc-ak-01', stateId: 'state-ak', stateName: 'Akwa Ibom', lgaId: 'lga-ak-uyo', lgaName: 'Uyo', name: 'Uyo', type: 'capital_city', isCapitalCity: true },
  { id: 'loc-ak-02', stateId: 'state-ak', stateName: 'Akwa Ibom', lgaId: 'lga-ak-uyo', lgaName: 'Uyo', name: 'Ewet Housing Estate', type: 'neighborhood' },
  { id: 'loc-ak-03', stateId: 'state-ak', stateName: 'Akwa Ibom', lgaId: 'lga-ak-eket', lgaName: 'Eket', name: 'Eket', type: 'city' },
  { id: 'loc-ak-04', stateId: 'state-ak', stateName: 'Akwa Ibom', lgaId: 'lga-ak-ikot-ekpene', lgaName: 'Ikot Ekpene', name: 'Ikot Ekpene (Raffia City)', type: 'city' },

  // --- CROSS RIVER STATE ---
  // Capital: Calabar (Calabar Municipal / Calabar South)
  { id: 'loc-cr-01', stateId: 'state-cr', stateName: 'Cross River', lgaId: 'lga-cr-calabar-municipal', lgaName: 'Calabar Municipal', name: 'Calabar', type: 'capital_city', isCapitalCity: true },
  { id: 'loc-cr-02', stateId: 'state-cr', stateName: 'Cross River', lgaId: 'lga-cr-calabar-municipal', lgaName: 'Calabar Municipal', name: 'State Housing Estate', type: 'neighborhood' },
  { id: 'loc-cr-03', stateId: 'state-cr', stateName: 'Cross River', lgaId: 'lga-cr-ikom', lgaName: 'Ikom', name: 'Ikom', type: 'town' },

  // --- PLATEAU STATE ---
  // Capital: Jos (Jos North / Jos South)
  { id: 'loc-pl-01', stateId: 'state-pl', stateName: 'Plateau', lgaId: 'lga-pl-jos-north', lgaName: 'Jos North', name: 'Jos', type: 'capital_city', isCapitalCity: true },
  { id: 'loc-pl-02', stateId: 'state-pl', stateName: 'Plateau', lgaId: 'lga-pl-jos-south', lgaName: 'Jos South', name: 'Bukuru', type: 'town' },
  { id: 'loc-pl-03', stateId: 'state-pl', stateName: 'Plateau', lgaId: 'lga-pl-jos-north', lgaName: 'Jos North', name: 'Rayfield', type: 'neighborhood' },

  // --- BORNO STATE ---
  // Capital: Maiduguri (Maiduguri LGA)
  { id: 'loc-bo-01', stateId: 'state-bo', stateName: 'Borno', lgaId: 'lga-bo-maiduguri', lgaName: 'Maiduguri', name: 'Maiduguri', type: 'capital_city', isCapitalCity: true },

  // --- SOKOTO STATE ---
  // Capital: Sokoto (Sokoto North / South)
  { id: 'loc-so-01', stateId: 'state-so', stateName: 'Sokoto', lgaId: 'lga-so-sokoto-north', lgaName: 'Sokoto North', name: 'Sokoto', type: 'capital_city', isCapitalCity: true },

  // --- BENUE STATE ---
  // Capital: Makurdi (Makurdi LGA)
  { id: 'loc-be-01', stateId: 'state-be', stateName: 'Benue', lgaId: 'lga-be-makurdi', lgaName: 'Makurdi', name: 'Makurdi', type: 'capital_city', isCapitalCity: true },
  { id: 'loc-be-02', stateId: 'state-be', stateName: 'Benue', lgaId: 'lga-be-gboko', lgaName: 'Gboko', name: 'Gboko', type: 'city' },
  { id: 'loc-be-03', stateId: 'state-be', stateName: 'Benue', lgaId: 'lga-be-otukpo', lgaName: 'Otukpo', name: 'Otukpo', type: 'city' },

  // --- IMO STATE ---
  // Capital: Owerri (Owerri Municipal / North / West)
  { id: 'loc-im-01', stateId: 'state-im', stateName: 'Imo', lgaId: 'lga-im-owerri-municipal', lgaName: 'Owerri Municipal', name: 'Owerri', type: 'capital_city', isCapitalCity: true },
  { id: 'loc-im-02', stateId: 'state-im', stateName: 'Imo', lgaId: 'lga-im-owerri-municipal', lgaName: 'Owerri Municipal', name: 'Ikenegbu Layout', type: 'neighborhood' },
  { id: 'loc-im-03', stateId: 'state-im', stateName: 'Imo', lgaId: 'lga-im-owerri-municipal', lgaName: 'Owerri Municipal', name: 'Aladinma', type: 'neighborhood' },
  { id: 'loc-im-04', stateId: 'state-im', stateName: 'Imo', lgaId: 'lga-im-orlu', lgaName: 'Orlu', name: 'Orlu', type: 'city' },
];

/**
 * HELPER FUNCTIONS
 */

export function getAllStates(): NigerianState[] {
  return NIGERIAN_STATES;
}

export function getStateByName(stateName?: string): NigerianState | undefined {
  if (!stateName) return undefined;
  const clean = stateName.trim().toLowerCase();
  const canonical = STATE_ALIASES[clean] || stateName;
  return NIGERIAN_STATES.find(
    (s) => s.name.toLowerCase() === canonical.toLowerCase() || s.code.toLowerCase() === clean
  );
}

export function getLgasForState(stateNameOrCode?: string): string[] {
  if (!stateNameOrCode) return [];
  const state = getStateByName(stateNameOrCode);
  if (!state) return [];
  return NIGERIAN_LGAS_MAP[state.name] || [];
}

export function getAreasForLga(stateName?: string, lgaName?: string): NigerianLocationArea[] {
  if (!stateName || !lgaName) return [];
  const state = getStateByName(stateName);
  const stateTarget = state?.name.toLowerCase() || stateName.toLowerCase();
  const lgaTarget = lgaName.toLowerCase().trim();

  return NIGERIAN_AREAS.filter(
    (a) =>
      a.stateName.toLowerCase() === stateTarget &&
      (a.lgaName.toLowerCase() === lgaTarget || lgaTarget.includes(a.lgaName.toLowerCase()))
  );
}

export const ALL_NIGERIAN_STATES = NIGERIAN_STATES;
export const NIGERIA_TOTAL_LGAS_COUNT = 774;

export function getCitiesForLga(stateName?: string, lgaName?: string): string[] {
  if (!stateName || !lgaName) return [];
  const areas = getAreasForLga(stateName, lgaName);
  return Array.from(new Set(areas.map((a) => a.name)));
}

export function getStateCapital(stateName?: string): string {
  const state = getStateByName(stateName);
  return state?.capital || '';
}

/**
 * Autocomplete search across State, LGA, Capital, and City/Area
 */
export interface LocationSearchResult {
  id: string;
  state: string;
  lga: string;
  cityArea?: string;
  displayText: string;
  subText: string;
  type: 'state' | 'lga' | 'city_area';
}

export function searchLocations(query: string, limit: number = 8): LocationSearchResult[] {
  if (!query || query.trim().length < 2) return [];
  const q = query.trim().toLowerCase();
  const results: LocationSearchResult[] = [];
  const seenKeys = new Set<string>();

  // 1. Search in known Cities/Areas (Level 3)
  for (const area of NIGERIAN_AREAS) {
    if (
      area.name.toLowerCase().includes(q) ||
      area.lgaName.toLowerCase().includes(q) ||
      area.stateName.toLowerCase().includes(q)
    ) {
      const key = `${area.stateName}|${area.lgaName}|${area.name}`;
      if (!seenKeys.has(key)) {
        seenKeys.add(key);
        results.push({
          id: area.id,
          state: area.stateName,
          lga: area.lgaName,
          cityArea: area.name,
          displayText: `${area.name}`,
          subText: `${area.lgaName} LGA, ${area.stateName}${area.isCapitalCity ? ' (State Capital)' : ''}`,
          type: 'city_area',
        });
      }
    }
  }

  // 2. Search in LGAs (Level 2)
  for (const [stateName, lgas] of Object.entries(NIGERIAN_LGAS_MAP)) {
    for (const lga of lgas) {
      if (lga.toLowerCase().includes(q)) {
        const key = `${stateName}|${lga}|`;
        if (!seenKeys.has(key)) {
          seenKeys.add(key);
          results.push({
            id: `lga-${stateName}-${lga}`.replace(/\s+/g, '-').toLowerCase(),
            state: stateName,
            lga,
            displayText: `${lga} LGA`,
            subText: `${stateName} State`,
            type: 'lga',
          });
        }
      }
    }
  }

  // 3. Search in States & Capitals (Level 1)
  for (const state of NIGERIAN_STATES) {
    if (state.name.toLowerCase().includes(q) || state.capital.toLowerCase().includes(q)) {
      const key = `${state.name}||`;
      if (!seenKeys.has(key)) {
        seenKeys.add(key);
        results.push({
          id: state.id,
          state: state.name,
          lga: '',
          displayText: `${state.name} State`,
          subText: `Capital: ${state.capital} (${state.zone})`,
          type: 'state',
        });
      }
    }
  }

  return results.slice(0, limit);
}

export function searchNigerianLocations(query: string, maxResults: number = 8): LocationSearchResult[] {
  return searchLocations(query, maxResults);
}

/**
 * Validates if an LGA belongs to a given state.
 * Returns true if valid, false if invalid.
 */
export function isValidLgaForState(stateName: string, lgaName: string): boolean {
  if (!stateName || !lgaName) return false;
  const lgas = getLgasForState(stateName);
  return lgas.some((l) => l.toLowerCase() === lgaName.toLowerCase().trim());
}

/**
 * Calculates approximate geographical distance in kilometers using the Haversine formula.
 * Used for internal proximity matching signals (exact coordinates are never exposed to customers).
 */
export function calculateHaversineDistanceKm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371; // Earth radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Total Statistics for Reporting & System Verification
 */
export function getNigeriaLocationStatistics() {
  let totalLgas = 0;
  for (const lgas of Object.values(NIGERIAN_LGAS_MAP)) {
    totalLgas += lgas.length;
  }
  return {
    totalStates: 36,
    hasFct: true,
    totalStateEntities: NIGERIAN_STATES.length, // 37
    totalLgas, // 774
    totalMappedAreas: NIGERIAN_AREAS.length,
  };
}
